import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import Logo from '../assets/images/Logo_blue_2x.svg';
import DemoToggle from '../components/DemoToggle';
import HomeIcon from '../components/svg/HomeIcon';
import QuestionIcon from '../components/svg/QuestionIcon';

import ArrowIcon from '../assets/images/arrowsInLineHorizontal.svg';

const SendIcon = ({ size = 'w-5 h-5', color = '#181D27' }) => (
  <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 2L11 13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const InvestmentsIcon = ({ size = 'w-5 h-5', color = '#181D27' }) => (
  <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3v18h18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 14l4-4 4 4 5-7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const WalletIcon = ({ size = 'w-5 h-5', color = '#181D27' }) => (
  <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 7V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="16" cy="13.5" r="1.4" fill={color}/>
  </svg>
);

const GoalIcon = ({ size = 'w-5 h-5', color = '#181D27' }) => (
  <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2"/>
    <circle cx="12" cy="12" r="5" stroke={color} strokeWidth="2"/>
    <circle cx="12" cy="12" r="1.6" fill={color}/>
  </svg>
);

const MoreIcon = ({ size = 'w-5 h-5', color = '#181D27' }) => (
  <svg className={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="5" cy="12" r="1.8" fill={color}/>
    <circle cx="12" cy="12" r="1.8" fill={color}/>
    <circle cx="19" cy="12" r="1.8" fill={color}/>
  </svg>
);

const ChevronIcon = ({ open, color = '#9CA3AF' }) => (
  <svg
    className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
    viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M6 9l6 6 6-6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Menu agrupado (Fase B): 6 entradas. Las RUTAS no cambian — los grupos solo
// organizan la navegacion. "Mi dinero" agrupa saldo + cuentas globales;
// "Invertir" agrupa acciones + rendimientos; "Mas" agrupa lo secundario.
const sidebarData = [
  {
    title: 'Inicio',
    icon: (size, color) => <HomeIcon size={size} color={color} />,
    path: '/dashboard',
  },
  {
    title: 'Mi dinero',
    icon: (size, color) => <WalletIcon size={size} color={color} />,
    children: [
      { title: 'Mi saldo', path: '/dashboard/assets' },
      { title: 'Cuentas Globales', path: '/dashboard/accounts' },
    ],
  },
  {
    title: 'Enviar',
    icon: (size, color) => <SendIcon size={size} color={color} />,
    path: '/dashboard/transfer',
  },
  {
    title: 'Invertir',
    icon: (size, color) => <InvestmentsIcon size={size} color={color} />,
    children: [
      { title: 'Acciones y ETFs', path: '/dashboard/investments' },
      { title: 'Mis Agentes', path: '/dashboard/agentes' },
      { title: 'Rendimientos', path: '/dashboard/rewards' },
    ],
  },
  {
    title: 'Mis Metas',
    icon: (size, color) => <GoalIcon size={size} color={color} />,
    path: '/dashboard/metas',
  },
  {
    title: 'Más',
    icon: (size, color) => <MoreIcon size={size} color={color} />,
    children: [
      { title: 'El mapa de mi dinero', path: '/dashboard/mapa' },
      { title: 'Cápsulas', path: '/dashboard/capsulas' },
      { title: 'Historial', path: '/dashboard/history' },
      { title: 'Extractos', path: '/dashboard/statements' },
      { title: 'Glosario', path: '/dashboard/glosario' },
    ],
  },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('/');
  const [openGroups, setOpenGroups] = useState({});

  // Activo por prefijo (cubre subrutas como /dashboard/metas/:id o /statements/:period).
  const isPathActive = (path) =>
    path === '/dashboard' ? activeTab === '/dashboard' : activeTab.startsWith(path);

  const groupHasActive = (item) => (item.children || []).some((c) => isPathActive(c.path));

  useEffect(() => {
    setActiveTab(location.pathname);
    // Auto-abrir el grupo cuya subruta esta activa.
    const updates = {};
    sidebarData.forEach((item) => {
      if (item.children && item.children.some((c) =>
        c.path === '/dashboard' ? location.pathname === '/dashboard' : location.pathname.startsWith(c.path)
      )) {
        updates[item.title] = true;
      }
    });
    if (Object.keys(updates).length) setOpenGroups((prev) => ({ ...prev, ...updates }));
  }, [location.pathname]);

  const onChangeRoute = (path) => {
    setActiveTab(path);
    // Misma ruta no remonta el page component. Un state nuevo cambia
    // location.key para que Agentes vuelva a la lista.
    if (location.pathname === path) {
      navigate(path, { replace: true, state: { _nav: Date.now() } });
      if (path === '/dashboard/agentes') {
        window.dispatchEvent(new Event('fp-agentes-reset'));
      }
    } else {
      navigate(path);
    }
  };

  const toggleGroup = (title) => {
    setOpenGroups((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  return (
    <div className="w-65 h-screen fx-shell-side bg-white border-r border-r-1 border-r-[#F5F5F5] p-6 flex flex-col justify-between">
      <div className="space-y-8">
        <DemoToggle>
          <div className="w-[155px] h-17">
            <img src={Logo} alt="App logo" />
          </div>
        </DemoToggle>
        <ul className="space-y-1">
          {sidebarData.map((data) => {
            // Item simple (sin hijos)
            if (!data.children) {
              const active = isPathActive(data.path);
              return (
                <li
                  key={data.title}
                  onClick={() => onChangeRoute(data.path)}
                  className={`px-4 py-3 w-52 rounded-[12px] cursor-pointer hover:bg-[#E6EBF7] flex items-center justify-center space-x-1 text-[#2A59C6] ${
                    active ? 'bg-[#E6EBF7]' : ''
                  }`}
                >
                  {data.icon('w-5 h-5', active ? '#2A59C6' : '#181D27')}
                  <p
                    className={`w-full text-sm leading-[20px] tracking-[0%] ${
                      active ? 'text-[#2A59C6] font-semibold' : 'text-[#181D27] font-normal'
                    }`}
                  >
                    {data.title}
                  </p>
                </li>
              );
            }

            // Grupo colapsable
            const open = !!openGroups[data.title];
            const groupActive = groupHasActive(data);
            return (
              <li key={data.title}>
                <div
                  onClick={() => toggleGroup(data.title)}
                  className={`px-4 py-3 w-52 rounded-[12px] cursor-pointer hover:bg-[#E6EBF7] flex items-center space-x-1 ${
                    groupActive && !open ? 'bg-[#E6EBF7]' : ''
                  }`}
                >
                  {data.icon('w-5 h-5', groupActive ? '#2A59C6' : '#181D27')}
                  <p
                    className={`w-full text-sm leading-[20px] tracking-[0%] ${
                      groupActive ? 'text-[#2A59C6] font-semibold' : 'text-[#181D27] font-normal'
                    }`}
                  >
                    {data.title}
                  </p>
                  <ChevronIcon open={open} color={groupActive ? '#2A59C6' : '#9CA3AF'} />
                </div>
                {open && (
                  <ul className="mt-1 space-y-1">
                    {data.children.map((child) => {
                      const childActive = isPathActive(child.path);
                      return (
                        <li
                          key={child.path}
                          onClick={() => onChangeRoute(child.path)}
                          className={`pl-11 pr-4 py-2.5 w-52 rounded-[12px] cursor-pointer hover:bg-[#E6EBF7] ${
                            childActive ? 'bg-[#E6EBF7]' : ''
                          }`}
                        >
                          <p
                            className={`text-sm leading-[20px] ${
                              childActive ? 'text-[#2A59C6] font-semibold' : 'text-[#535862] font-normal'
                            }`}
                          >
                            {child.title}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="border-t-1 border-t-[#D5D7DA]">
        <button
          onClick={() => onChangeRoute('/dashboard/contact')}
          className={`p-4 py-3 w-52 cursor-pointer hover:bg-[#E6EBF7] flex items-center justify-center space-x-1 ${
            activeTab == '/contact' ? 'bg-[#E6EBF7]' : ''
          }`}
        >
          <QuestionIcon color={activeTab == '/dashboard/contact' ? '#2A59C6' : '#181D27'} />
          <p
            className={`w-full text-sm font-normal leading-[20px] tracking-[0%] text-left ${
              activeTab == '/contact'
                ? 'text-[#2A59C6] font-semibold'
                : 'text-[#181D27] font-normal'
            }`}
          >
            Ayuda
          </p>
        </button>
        <button className="p-4 py-3 w-52 cursor-pointer hover:bg-[#E6EBF7] flex items-center justify-center space-x-1">
          <img src={ArrowIcon} alt="Arrow Icon" className="w-5 h-5" />
          <p className="w-full text-sm font-normal leading-[20px] tracking-[0%] text-[#181D27] text-left">
            Colapsar
          </p>
        </button>
      </div>
    </div>
  );
}
